import {
  asId,
  type AvatarColor,
  type ChannelId,
  type ConversationId,
  type NotificationCounts,
  type SidebarResponse,
  type SidebarTeamNode,
  type TeamId,
  type TenantId,
  type User,
  type UserId,
} from '@cyphex/types';

/* Deterministic fixtures mirroring the approved reference screen. Shapes are
 * the REAL wire contracts — swapping `mockShellApi` for `httpShellApi` in
 * `lib/api/shell.ts` is the only change needed to go live. */

const NOW = '2026-07-31T10:30:00.000Z';
const stamps = { createdAt: NOW, updatedAt: NOW };

interface ChannelSeed {
  name: string;
  unread?: number;
  mentions?: number;
  kind?: 'standard' | 'announcement' | 'private';
}

function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function makeChannels(teamId: TeamId, seeds: readonly ChannelSeed[]): SidebarTeamNode['channels'] {
  return seeds.map((seed, index) => {
    const slug = slugify(seed.name);
    const count = seed.unread ?? 0;
    return {
      id: asId<ChannelId>(`${teamId}-ch-${slug}`),
      teamId,
      conversationId: asId<ConversationId>(`${teamId}-cv-${slug}`),
      name: seed.name,
      slug,
      kind: seed.kind ?? 'standard',
      avatarColor: 'violet',
      isDefault: index === 0,
      unread: {
        count,
        mentionCount: seed.mentions ?? 0,
        hasUnread: count > 0,
        lastReadSeq: 0,
      },
      isFavorite: false,
      sortOrder: index,
    };
  });
}

function makeTeam(
  id: string,
  name: string,
  subtitle: string,
  initials: string,
  color: AvatarColor,
  options: { pinned?: boolean; expanded?: boolean; channels?: readonly ChannelSeed[] } = {},
): SidebarTeamNode {
  const teamId = asId<TeamId>(id);
  return {
    id: teamId,
    name,
    slug: slugify(name),
    subtitle,
    initials,
    avatarColor: color,
    avatarUrl: null,
    visibility: 'private',
    membership: {
      role: 'member',
      state: 'active',
      isPinned: options.pinned ?? false,
      isFavorite: false,
      isExpanded: options.expanded ?? false,
      sortOrder: null,
      notificationLevel: null,
      mutedUntil: null,
      joinedAt: NOW,
    },
    channels: makeChannels(teamId, options.channels ?? []),
  };
}

export const MOCK_SIDEBAR: SidebarResponse = {
  pinnedTeams: [
    makeTeam('team-pd', 'Product Design', 'Cyphex Technologies', 'PD', 'teal', { pinned: true }),
    makeTeam('team-mt', 'Marketing Team', 'Marketing', 'MT', 'pink', { pinned: true }),
    makeTeam('team-ds', 'DevOps Squad', 'Engineering', 'DS', 'blue', { pinned: true }),
    makeTeam('team-ld', 'Leadership', 'Company', 'LD', 'orange', { pinned: true }),
  ],
  teams: [
    makeTeam('team-cyphex', 'Cyphex Technologies', 'Organisation', 'C', 'orange', {
      expanded: true,
      channels: [
        { name: 'General' },
        { name: 'Announcements', unread: 1, mentions: 1, kind: 'announcement' },
        { name: 'Project Phoenix 🚀' },
        { name: 'Product Design' },
        { name: 'DevOps' },
        { name: 'QA Engineering' },
      ],
    }),
    makeTeam('team-marketing', 'Marketing Team', 'Marketing', 'MT', 'pink', {
      channels: [{ name: 'Campaigns' }, { name: 'Brand' }, { name: 'Events' }],
    }),
    makeTeam('team-sales', 'Sales & Business', 'Revenue', 'SB', 'green', {
      channels: [{ name: 'Pipeline', unread: 3 }, { name: 'Enterprise' }],
    }),
    makeTeam('team-hr', 'HR Department', 'People', 'HR', 'indigo', {
      channels: [{ name: 'Hiring' }, { name: 'Onboarding' }],
    }),
    makeTeam('team-support', 'Support Team', 'Customer Success', 'ST', 'violet', {
      channels: [{ name: 'Escalations', unread: 2 }, { name: 'Tier 1' }],
    }),
  ],
  badges: { activity: 12, chat: 5, approvals: 0, calls: 0 },
};

export const MOCK_VIEWER: User = {
  ...stamps,
  id: asId<UserId>('user-vamshi'),
  tenantId: asId<TenantId>('tenant-cyphex'),
  displayName: 'Vamshi Uppala',
  username: 'vamshi',
  type: 'member',
  initials: 'VU',
  avatarColor: 'indigo',
  avatarUrl: null,
  jobTitle: 'Head of Platform',
  email: 'vamshi@cyphextechnologies.com',
  emailVerifiedAt: NOW,
  status: 'active',
  role: 'admin',
  givenName: 'Vamshi',
  familyName: 'Uppala',
  department: 'Engineering',
  officeLocation: 'Hyderabad',
  phone: null,
  timezone: 'Asia/Kolkata',
  locale: 'en-IN',
  preferences: { theme: 'system', density: 'comfortable', enterToSend: true },
  botAppId: null,
  lastActiveAt: NOW,
  presence: {
    state: 'available',
    since: NOW,
    statusLine: 'Working on Project Phoenix',
    activityState: null,
  },
};

export const MOCK_NOTIFICATION_COUNTS: NotificationCounts = {
  unseen: 8,
  unread: 8,
  mentions: 3,
  byKind: { mention: 3, message: 4, approval_request: 1 },
};

/** People surfaced by the command palette's "people" section. */
export const MOCK_PEOPLE = [
  { id: 'user-rohit', name: 'Rohit Sharma', subtitle: 'Product Design · Available' },
  { id: 'user-anjali', name: 'Anjali Mehta', subtitle: 'Marketing · In a meeting' },
  { id: 'user-vikram', name: 'Vikram Reddy', subtitle: 'DevOps Squad · Available' },
  { id: 'user-neha', name: 'Neha Kulkarni', subtitle: 'Leadership · Away' },
  { id: 'user-arjun', name: 'Arjun Nair', subtitle: 'Support Team · Offline' },
] as const;

/** Files surfaced by the command palette's "files" section. */
export const MOCK_FILES = [
  { id: 'file-prd', name: 'PRD - Project Phoenix.pdf', subtitle: 'Cyphex Technologies' },
  { id: 'file-ds', name: 'Design System v2.0', subtitle: 'Product Design' },
  { id: 'file-deploy', name: 'Deployment Guide.docx', subtitle: 'DevOps Squad' },
  { id: 'file-notes', name: 'Meeting Notes - 20 Jun.md', subtitle: 'General' },
] as const;
